import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useMemo} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {
  useGetDirectSaleQuery,
  useGetFastTrackIssueQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

const AssignReceivedByFormSchema = z.object({
  receivedBy: z.string(),
});

type AssignReceivedByForm = z.infer<typeof AssignReceivedByFormSchema>;

interface AssignReceivedByProps {
  fastTrackIssueId: string;
  onReceiverSelect: (receiver: string) => void;
}

export function AssignReceivedBy(props: AssignReceivedByProps) {
  const [, {isLoading: isActionLoading}] = usePutWarehouseHeaderDynamicActionsMutation({
    fixedCacheKey: 'fast-track-header-action',
  });

  const {data: fastTrackIssue, isLoading: isFastTrackIssueLoading} = useGetFastTrackIssueQuery(
    {fastTrackId: props.fastTrackIssueId},
    {skip: isNil(props.fastTrackIssueId)}
  );

  const serviceCaseId = fastTrackIssue?.originEntityHeader?.serviceOrder?.serviceCaseId;
  const directSaleId = fastTrackIssue?.originEntityHeader?.directSale?.directSaleId;

  const {data: serviceCase, isLoading: isServiceCaseLoading} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: serviceCaseId as string},
    {skip: isNil(serviceCaseId)}
  );

  const {data: directSale, isLoading: isDirectSaleLoading} = useGetDirectSaleQuery(
    {directSaleId: directSaleId as string},
    {skip: isNil(directSaleId)}
  );

  const authorizationProfileId = isNotNil(serviceCaseId)
    ? serviceCase?.authorizationProfileId
    : directSale?.authorizationProfileId;

  const {data: mechanics, isLoading: areMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: authorizationProfileId as string,
      isActive: true,
      withToOffer: true,
    },
    {
      skip: isNilOrEmpty(authorizationProfileId),
    }
  );

  const mechanicOptions = useMemo(
    () =>
      mechanics?.map((mechanic) => ({
        label: mechanic?.name,
        value: mechanic?.employeeId,
      })) ?? [],
    [mechanics]
  );

  const defaultValues: AssignReceivedByForm = {
    receivedBy: fastTrackIssue?.receivedBy ?? '',
  };

  const handleSubmit: FormSubmitHandler<AssignReceivedByForm> = async (formValues) =>
    await props.onReceiverSelect(formValues.receivedBy);

  const isLoading =
    isFastTrackIssueLoading || isServiceCaseLoading || isDirectSaleLoading || areMechanicsLoading;

  return (
    <DataStatus isLoading={isLoading}>
      <Form<AssignReceivedByForm>
        defaultValues={defaultValues}
        experimentalZodSchema={AssignReceivedByFormSchema}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="receivedBy"
              type="choice"
              options={mechanicOptions}
              isLoading={areMechanicsLoading}
              isRequired
              isDisabled={isNotNilOrEmpty(fastTrackIssue?.receivedBy)}
            />
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={() => closeCurrentDialog()}
                isLoading={isActionLoading}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('general.actions.confirm')}
                isDisabled={isNotNilOrEmpty(fastTrackIssue?.receivedBy)}
                isLoading={isActionLoading}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
